import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon, MapIcon, PresentationIcon } from 'lucide-react';
import { chapters, entry } from '../../data/journey';
interface EntryScreenProps {
  onStartCampaign: (snak: 1 | 2 | 3) => void;
  onStartPresentation: () => void;
  onOpenJourney: () => void;
}
const EASE = [0.23, 1, 0.32, 1] as const;
const DOT: Record<string, string> = {
  none: 'bg-line',
  colour: 'bg-ozBlue',
  full: 'bg-ozGreen'
};
export function EntryScreen({
  onStartCampaign,
  onStartPresentation,
  onOpenJourney
}: EntryScreenProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  return <AnimatePresence mode="wait" data-id="element-203" data-mp="2y15g2vyimjjg">
      {!menuOpen ? <motion.div key="cover" initial={{
      opacity: 0,
      y: 8
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.24,
      ease: EASE
    }} className="flex flex-1 flex-col justify-center" data-id="element-204" data-mp="2y1nmglew6e53">
          <p className="text-[9.5px] uppercase tracking-micro text-blue" data-id="element-205" data-mp="3f2j90ss21qd3">{entry.eyebrow}</p>

          <h2 className="mt-4 font-display text-[32px] font-semibold leading-[1.02] tracking-tight text-navy" data-id="element-206" data-mp="3j2ba6kf4cu8f">
            {entry.headline[0]}
            <br data-id="element-207" data-mp="3o48z9va7aj9a" />
            {entry.headline[1]}
          </h2>

          <p className="mt-4 text-[13px] leading-relaxed text-mut" data-id="element-208" data-mp="3t2af87cby2gj">{entry.lead}</p>

          <div className="mt-8 flex flex-col gap-2" data-id="element-209" data-mp="1vr935hfv0ok">
            <button type="button" onClick={() => setMenuOpen(true)} className="flex h-12 items-center justify-between gap-2 rounded-full bg-navy px-5 text-left transition-opacity duration-150 hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue focus-visible:ring-offset-2" data-id="element-210" data-mp="1svadexro7z2">
              <span data-id="element-211" data-mp="kmidrtqhe2wm">
                <span className="block text-[13.5px] font-semibold leading-none text-paper" data-id="element-212" data-mp="kn0h1abk9he1">
                  {entry.openLabel}
                </span>
                <span className="mt-1 block text-[10px] leading-none text-sky" data-id="element-213" data-mp="3w19axwjvnwmz">
                  {entry.openDetail}
                </span>
              </span>
              <ArrowRightIcon className="h-4 w-4 shrink-0 text-mint" aria-hidden="true" data-id="element-214" data-mp="3r0lbcmhl27h5" />
            </button>

            <button type="button" onClick={onOpenJourney} className="flex h-11 items-center justify-center gap-2 rounded-full border border-line text-[12.5px] font-medium text-navy transition-colors duration-150 hover:border-blue focus:outline-none focus-visible:ring-2 focus-visible:ring-blue" data-id="element-215" data-mp="9o1327fqg3bb">
              <MapIcon className="h-3.5 w-3.5 text-blue" aria-hidden="true" data-id="element-216" data-mp="sk0t3cp5gws9" />
              {entry.journeyLabel}
            </button>
          </div>

          <p className="mt-auto pt-6 text-[9px] leading-snug text-mut" data-id="element-217" data-mp="nk4obb8mp1bn">{entry.footnote}</p>
        </motion.div> : <motion.div key="menu" initial={{
      opacity: 0,
      y: 8
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.24,
      ease: EASE
    }} className="flex flex-1 flex-col" data-id="element-218" data-mp="2pdvn45tjifdo">
          <button type="button" onClick={() => setMenuOpen(false)} className="flex items-center gap-1.5 self-start text-[11px] text-mut transition-colors duration-150 hover:text-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-blue" data-id="element-219" data-mp="19wh5wl8u61er">
            <ArrowLeftIcon className="h-3 w-3" aria-hidden="true" data-id="element-220" data-mp="m6wpoa42hi96" />
            {entry.backLabel}
          </button>

          <h2 className="mt-3 font-display text-[20px] font-semibold leading-snug text-navy" data-id="element-221" data-mp="c6ma4iuk7yqu">
            {entry.menuTitle}
          </h2>

          <p className="mt-4 text-[9px] uppercase tracking-micro text-mut" data-id="element-222" data-mp="h6s62b7v47eq">{entry.campaignLabel}</p>

          <ul className="mt-2.5 flex flex-col gap-1.5" data-id="element-223" data-mp="26loxa9w4z4ql">
            {chapters.map((chapter, i) => <motion.li key={chapter.snak} initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.22,
          delay: 0.04 + i * 0.05,
          ease: EASE
        }} data-id="element-224" data-mp="2bioya6e4phau" data-mpi={i}>
                <button type="button" onClick={() => onStartCampaign(chapter.snak)} className="flex w-full items-start gap-3 rounded-xl border border-line bg-mist px-3.5 py-3 text-left transition-colors duration-150 hover:border-blue focus:outline-none focus-visible:ring-2 focus-visible:ring-blue" data-id="element-225" data-mp="2gsm3uqn44xs">
                  <span className="min-w-0 flex-1" data-id="element-226" data-mp="3jvzd95ku0b15">
                    <span className="flex items-center gap-2" data-id="element-227" data-mp="3exftnbtpnbby">
                      <span className={`h-2 w-2 shrink-0 rounded-full ${DOT[chapter.brand]}`} aria-hidden="true" data-id="element-228" data-mp="3evuvzdc3nc9v" />
                      <span className="font-display text-[13.5px] font-semibold leading-none tracking-tight text-navy" data-id="element-229" data-mp="35zlxc0vy4hos">
                        {chapter.archetype}
                      </span>
                      <span className="text-[9px] uppercase tracking-micro text-mut" data-id="element-230" data-mp="1gg5nsh49jggq">
                        {chapter.label}
                      </span>
                    </span>
                    <span className="mt-1.5 block text-[11.5px] leading-snug text-navy2" data-id="element-231" data-mp="1qfwe06lsj9b0">
                      {chapter.verb} · {chapter.differentiation}
                    </span>
                    <span className="mt-1 block text-[10.5px] leading-snug text-mut" data-id="element-232" data-mp="xqz00fom3yo2">
                      {chapter.audience}
                    </span>
                    <span className="mt-1.5 block text-[9px] uppercase tracking-micro text-mut" data-id="element-233" data-mp="12mmmgnbpmdpv">
                      {chapter.brandNote}
                    </span>
                  </span>
                  <ArrowRightIcon className="mt-1 h-4 w-4 shrink-0 text-navy2" aria-hidden="true" data-id="element-234" data-mp="1bjf3tipc80q1" />
                </button>
              </motion.li>)}
          </ul>

          <div className="mt-auto pt-5" data-id="element-235" data-mp="izgniruyom9g">
            <button type="button" onClick={onStartPresentation} className="flex w-full items-center gap-3 rounded-xl bg-navy px-4 py-3 transition-opacity duration-150 hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue focus-visible:ring-offset-2" data-id="element-236" data-mp="1mbnp2u1dys1a">
              <PresentationIcon className="h-4 w-4 shrink-0 text-mint" aria-hidden="true" data-id="element-237" data-mp="1r3oz09b306o5" />
              <span className="min-w-0 flex-1 text-left" data-id="element-238" data-mp="13c9tk7o0d98o">
                <span className="block text-[12.5px] font-semibold leading-snug text-paper" data-id="element-239" data-mp="13ew6kmto4gya">
                  {entry.presentationLabel}
                </span>
                <span className="mt-0.5 block text-[10.5px] leading-snug text-sky" data-id="element-240" data-mp="1cej3owfd70lj">
                  {entry.presentationDetail}
                </span>
              </span>
            </button>
          </div>
        </motion.div>}
    </AnimatePresence>;
}